import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Avatar, BottomNav, Icon, fmtCoins } from "../ui";
import api from "../../services/api";
import { SIDEBAR_NAV, SIDEBAR_NAV_2, BOTTOM_NAV } from "./nav";

export function Brand() {
  return (
    <Link to="/" className="brand" aria-label="AITalk หน้าแรก">
      <span className="brand__logo"><Icon name="chat" size={18} strokeWidth={2.4} /></span>
      AITalk
    </Link>
  );
}

function Sidebar() {
  const { isSuperUser } = useAuth();
  const [chats, setChats] = useState([]);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    api.chats.list().then((rows) => alive && setChats((rows || []).slice(0, 5))).catch(() => {});
    return () => { alive = false; };
  }, [pathname]);

  const link = (it) => (
    <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) => `nav__link${isActive ? " is-active" : ""}`}>
      <span className="nav__icon"><Icon name={it.icon} size={19} /></span>
      {it.label}
    </NavLink>
  );
  return (
    <aside className="sidebar" aria-label="เมนูด้านข้าง">
      <Brand />
      <nav className="nav">{SIDEBAR_NAV.map(link)}</nav>
      {chats.length ? (
        <>
          <div className="sidebar__section">แชทล่าสุด</div>
          <nav className="nav">
            {chats.map((c) => (
              <NavLink key={c.id} to={`/chat/${c.id}`} className={({ isActive }) => `nav__link${isActive ? " is-active" : ""}`}>
                <Avatar name={c.name} seed={c.characterId} colors={c.colors} src={c.image} size={26} character />
                <span className="clamp-1">{c.name}</span>
              </NavLink>
            ))}
          </nav>
        </>
      ) : null}
      <div className="sidebar__section">บัญชี</div>
      <nav className="nav">
        {SIDEBAR_NAV_2.map(link)}
        {isSuperUser ? link({ to: "/admin", label: "ผู้ดูแลระบบ", icon: "shield" }) : null}
      </nav>
      <div className="sidebar__foot">
        <Link to="/terms">ข้อกำหนด & ความเป็นส่วนตัว</Link>
        <span>ตัวละครทั้งหมดเป็นเรื่องแต่ง</span>
      </div>
    </aside>
  );
}

function Topbar() {
  const { profile, coins, logout, isSuperUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    api.notifications.list().then((rows) => alive && setUnread((rows || []).some((n) => !n.isRead))).catch(() => {});
    return () => { alive = false; };
  }, [pathname]);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header className="topbar">
      <Brand />
      <div className="topbar__spacer" />
      <div className="coin-pill" aria-label={`คอยน์คงเหลือ ${coins}`}>
        <span className="coin"><i className="coin__dot" aria-hidden="true" />{fmtCoins(coins)}</span>
        <Link to="/coins" className="btn btn--sm">เติม</Link>
      </div>
      <Link to="/notifications" className="icon-btn" aria-label="แจ้งเตือน">
        <Icon name="bell" />
        {unread ? <span className="dot" /> : null}
      </Link>
      <div className="rel" ref={ref}>
        <button className="icon-btn" aria-label="เมนูผู้ใช้" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={{ padding: 0 }}>
          <Avatar name={profile?.displayName || profile?.email} size={40} />
        </button>
        {open ? (
          <div className="menu" role="menu">
            <div style={{ padding: "8px 12px" }}>
              <div className="strong clamp-1">{profile?.displayName || "ผู้ใช้"}</div>
              <div className="faint clamp-1" style={{ fontSize: 12 }}>{profile?.email}</div>
            </div>
            <hr />
            <Link to="/me"><Icon name="user" size={18} />โปรไฟล์</Link>
            <Link to="/club"><Icon name="crown" size={18} />คลับ VIP</Link>
            <Link to="/rewards"><Icon name="gift" size={18} />รางวัล & ชวนเพื่อน</Link>
            <Link to="/settings"><Icon name="settings" size={18} />ตั้งค่า</Link>
            {isSuperUser ? <Link to="/admin"><Icon name="shield" size={18} />ผู้ดูแลระบบ</Link> : null}
            <hr />
            <button onClick={doLogout}><Icon name="logout" size={18} />ออกจากระบบ</button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

export default function AppShell() {
  const { pathname } = useLocation();
  const flush = pathname.startsWith("/chat") || pathname.startsWith("/shorts");
  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        <Topbar />
        <main className={`content${flush ? " content--flush" : ""}`} id="main">
          <Outlet />
        </main>
      </div>
      <div className="hide-desktop">
        <BottomNav items={BOTTOM_NAV} isActive={(it) => it.match(pathname)} />
      </div>
    </div>
  );
}
